import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Injector,
    Input,
    InputSignal,
    OnInit,
    Output,
    Signal,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    FormControl,
    FormControlStatus,
    FormGroup,
    ReactiveFormsModule,
} from '@angular/forms';
import { DatiqTemplateModule } from '@dat/datiq-components-pkg/template';
import { mdIconLabel } from '@dat/iq-style-processor';
import { IqTooltipTooltipComponent } from '@dat/iq-tooltip';

import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';

@Component({
    selector: 'iq-control-checkbox-control',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        DatiqTemplateModule,
        IqTooltipTooltipComponent,
    ],
    templateUrl: './iq-control-checkbox-control.component.html',
    styleUrl: './iq-control-checkbox-control.component.scss',
})
export class IqControlCheckboxControlComponent implements OnInit {
    id: InputSignal<string> = input('');
    name: InputSignal<string> = input('');
    checkedLabel: InputSignal<string> = input('');
    uncheckedLabel: InputSignal<string> = input('');
    disabledLabel: InputSignal<string> = input('');
    disabled: InputSignal<boolean> = input(false);
    value: InputSignal<boolean> = input(false);
    tooltipLabel: InputSignal<string> = input('');
    tooltipHeader: InputSignal<string> = input('');
    tooltipBody: InputSignal<string> = input('');
    testId: InputSignal<string> = input('');
    private _events: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    currentTooltipLabel: Signal<string> = computed(() => {
        return this.tooltipLabel()?.length
            ? this.tooltipLabel()
            : `<span class="!text-size-[12px]">${mdIconLabel('help')}</span>`;
    });

    currentTestId: Signal<string> = computed(() => {
        return !!this.testId()?.length ? this.testId() : this.id();
    });

    currentLabel: Signal<string> = computed(() => {
        if (this.inputStatus() === 'DISABLED') return this.disabledLabel();

        return this.inputControlValue()
            ? this.checkedLabel()
            : this.uncheckedLabel();
    });

    @Input()
    fg: FormGroup = new FormGroup({});

    @Output()
    changed: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output()
    clicked: EventEmitter<boolean> = new EventEmitter<boolean>();

    currentValue: Signal<boolean> = computed(() => {
        return this.value();
    });

    showTooltip: Signal<boolean> = computed(() => {
        return !!this.tooltipHeader()?.length && !!this.tooltipBody()?.length;
    });

    inputControlValue: Signal<boolean> = signal(false);
    inputControl = this._fb.control(this.value());
    inputStatus: Signal<FormControlStatus> = signal('VALID');

    constructor(
        private readonly _injector: Injector,
        private readonly _fb: FormBuilder,
    ) {
        effect(() => {
            if (this.disabled()) return null;

            const value = this.value();

            untracked(() => {
                this.inputControl.setValue(value);
            });

            this.changed.emit(value);
        });

        effect(() => {
            untracked(() => {
                this.inputControl?.[this.disabled() ? 'disable' : 'enable']();
            });
        });
    }

    ngOnInit() {
        this.fg.addControl(this.name(), this._fb.control(this.value()));
        this.inputControl = this.fg.get(this.name()) as FormControl;
        this.inputControl.setValue(this.value());
        this.inputControlValue = toSignal(this.inputControl.valueChanges, {
            injector: this._injector,
        });
        this.inputStatus = toSignal(this.inputControl.statusChanges, {
            injector: this._injector,
        });
    }

    change(value: boolean) {
        if (!this.disabled()) return null;

        this.changed.emit(value);

           /**
         * This dispatches an event indicating the checkbox was updated
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onChange": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "control.id",
         *                "operator": "eq",
         *                "value": "CONTROL_ID"
         *            },
         *            {
         *                "path": "control.name",
         *                "operator": "eq",
         *                "value": "NAME"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ]
         * }
         * ```
         */
           const dispatch = () =>
            this._events.dispatch({
                name: 'type:change',
                payload: {
                    id: this.id(),
                    componentName: 'IqControlCheckboxControl',
                    ...this._createEmitData(),
                    value: !value
                },
            });

        dispatch();
    }

    click() {
        const currentValue = this.inputControl.value;

        this.inputControl.setValue(!currentValue);
        

        /**
         * This dispatches an event indicating the checkbox was clicked
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onClick": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "control.id",
         *                "operator": "eq",
         *                "value": "CONTROL_ID"
         *            },
         *            {
         *                "path": "control.name",
         *                "operator": "eq",
         *                "value": "NAME"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ]
         * }
         * ```
         */
        const dispatch = () =>
            this._events.dispatch({
                name: 'type:click',
                payload: {
                    id: this.id(),
                    componentName: 'IqControlCheckboxControl',
                    ...this._createEmitData(),
                    value: !currentValue
                },
            });

        dispatch();
    }

    private _createEmitData(): { [key: string]: any } {
        const emitData = {
            form: {
                ...this.fg.value,
                valid: this.fg.valid,
                errors: this.fg.errors,
            },
            control: {
                id: this.id(),
                value: this.value(),
                name: this.name(),
                type: 'checkbox',
            },
        };
        return emitData;
    }
}
